import type { CrudFilters } from '@refinedev/core';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// `extend` is idempotent, so this is safe next to the app-wide plugins.
dayjs.extend(customParseFormat);

export const DAY_FORMAT = 'YYYY-MM-DD';

const isDay = (value: string): boolean =>
  dayjs(value, DAY_FORMAT, true).isValid();

const shiftDay = (value: string, days: number): string =>
  dayjs(value, DAY_FORMAT, true).add(days, 'day').format(DAY_FORMAT);

/**
 * The day after `day`, i.e. the exclusive end of a range that includes it.
 * Returns `value` unchanged when it is not a plain day.
 */
export const nextDay = (value: string): string =>
  isDay(value) ? shiftDay(value, 1) : value;

/** Inverse of `nextDay`: the last day an exclusive upper bound includes. */
export const previousDay = (value: string): string =>
  isDay(value) ? shiftDay(value, -1) : value;

/**
 * A whole-day range, as filters.
 *
 * The upper bound is **exclusive**. `created_at` and `updated_at` are
 * timestamps, so `<= '2026-08-26'` is read as that day's midnight and matches
 * nothing that happened during the day — "created today" came back empty.
 * `< 2026-08-27` covers the whole day, and means the same thing on the
 * date-typed columns (`started_at`, `ended_at`), so every dimension can use it.
 */
export const dayRangeFilters = (
  field: string,
  from: string,
  to: string
): CrudFilters => [
  { field, operator: 'gte', value: from },
  { field, operator: 'lt', value: nextDay(to) },
];
